import type { PaletteOptions } from "@mui/material/styles";

/**
 * The Professional palette — the single definition of every colour in the app.
 *
 * Its own module rather than an inline block in `Theme-variable.tsx` because
 * `tokens.ts` reads `tint` and `avatarHues` off it, and importing
 * `Theme-variable.tsx` from `tokens.ts` would be a cycle
 * (`Theme-variable` → `Override` → `tokens`).
 */
const palette = {
  mode: "light",
  /**
   * Professional's teal. `main` for buttons, links, active text and progress
   * fill; `dark` for FABs, filled step dots and hover; `darker` for banners
   * and the filled track bar.
   */
  primary: {
    main: "#007E8C",
    light: "#3AA3AE",
    dark: "#005E69",
    darker: "#0D3D47",
    contrastText: "#ffffff",
  },
  /**
   * Professional collapses `secondary` onto the deep band `#0D3D47` — the
   * same value `primary.darker` carries. Kept as its own slot because
   * `secondary` has ~95 call sites and `primary.darker` is a ramp stop; the
   * two mean different things even at the same hex.
   */
  secondary: {
    main: "#0D3D47",
    light: "#3E5354",
    dark: "#000507",
    contrastText: "#ffffff",
  },
  /**
   * The dark surface ramp: `dark` is the sidebar and app header band,
   * `darkest` the active sidebar row, `medium` a lighter stop. Unchanged from
   * Playful — Professional specifies the same shell tones.
   */
  neutral: {
    medium: "#3E5354",
    dark: "#222F30",
    darkest: "#16201F",
  },
  /**
   * Growth actions ONLY — invite, share, celebrate, section eyebrows. Deeper
   * and browner than Playful's `#E0642A`. If it is not a growth action, it is
   * not accent.
   */
  accent: {
    main: "#AE4C1C",
    light: "#C86A3B",
    dark: "#8E3D15",
    contrastText: "#FFFFFF",
  },
  /** Logo and brand marks. Also the informational blue's source. */
  brand: {
    logo: "#2274A5",
  },
  success: {
    main: "#3E8F77",
    light: "#6FB39F",
    dark: "#2F7D67",
    contrastText: "#ffffff",
  },
  info: {
    main: "#2274A5",
    light: "#5B9BC6",
    dark: "#1A5C84",
    contrastText: "#ffffff",
  },
  /**
   * `main` is the lighter alert red, for banners and "Past Due" text. `dark`
   * is the destructive-action red a contained `color="error"` button takes —
   * see `Override.tsx`'s `MuiButton` `variants` (contained-error style).
   */
  error: {
    main: "#E57373",
    light: "#F09B9B",
    dark: "#A83226",
    contrastText: "#ffffff",
  },
  warning: {
    main: "#D98E12",
    light: "#E8AE50",
    dark: "#A96C08",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#16211F",
    secondary: "#5C6667",
    disabled: "#87908F",
  },
  /**
   * The neutral ramp, mapped onto MUI's numeric `grey` slots. Each slot keeps
   * the ROLE its comment describes and takes Professional's tone for that
   * role, so the ~30 `grey[…]` call sites outside `src/theme/` keep meaning
   * what they meant.
   *
   * Two slots land on the same `#F4F3F0`: the ramp has one inset-surface tone
   * and this ladder had two near-identical light greys for it.
   */
  grey: {
    /** Card hairline, and the border/backing tint for avatars and logos. */
    50: "#DEDEDE",
    /** Table header, input prefix box. */
    100: "#F4F4F4",
    /** Selected-state background. Ramp: inset panel. */
    200: "#F4F3F0",
    /** Disabled-state background. Ramp: disabled field fill. */
    300: "#E7E5E0",
    /** Input border, scrollbar thumb, disabled line, empty step dots. */
    400: "#B0ADA4",
    /** Disabled text, meta, captions on white. */
    500: "#87908F",
    /** Icon glyphs, table cells, secondary rows. */
    600: "#4A5556",
    /** Inset surface — the date-range chips. Same ramp tone as `200`. */
    700: "#F4F3F0",
    /** Long-form body copy inside cards. */
    800: "#2A3237",
  },
  background: {
    /**
     * Professional's page ground: a neutral soft grey, not Playful's warm
     * greige. Cards stay white — the `MuiPaper` override pins `common.white` —
     * so every Paper-backed surface lifts off the ground.
     */
    default: "#ECECEC",
    paper: "#FFFFFF",
  },
  /** The hairline on every surface. */
  divider: "#DEDEDE",
  /**
   * Status tint pairs — one background/foreground pair per status, shared by
   * chips, alert rules, stance tags, confidence badges and toasts so a status
   * reads identically wherever it appears.
   *
   * This is the single definition. `tokens.designSystem.statusTints` derives
   * from it (see `tokens.ts`) rather than restating the values, so there is
   * one place to edit.
   *
   * The four severity pairs — `success`, `error`, `warning`, `info` — are named
   * for the severity itself, not for a domain status, so a consumer that already
   * has a severity indexes straight in (`tint[severity]`) instead of translating
   * through a lookup table. They were `published`/`pastDue`/`draft`, which meant
   * the toast had to know that a success toast draws the "published" pair.
   *
   * `archived`, `selected` and `count` keep their domain names: they are not
   * severities, and `default`/`primary`/`accent` would say less than the role
   * they actually fill.
   *
   * The four severity pairs also carry `bd`, the tinted border a toast draws.
   * The other three have none: nothing draws a tinted border for them, and
   * inventing one would break the rule stated at the top of `tokens.ts` (no
   * value without a real call site).
   *
   * `bd` is the pair mixed ~14% from `bg` toward `fg`. The toast design doc
   * ("Tint-pair toast") states 18%, but none of the three borders it supplies
   * matches 18% — all three sit at ≈14%, so 14% is what the fourth is derived
   * at. See `bd` on `info` below.
   */
  tint: {
    /** MUI `success`. Also the "Published" status. */
    success: { bg: "#E6F1EE", fg: "#2F6E5C", bd: "#C9DFD8" },
    /** MUI `error`. Also the "Past due" status. */
    error: { bg: "#FAEAE8", fg: "#A83226", bd: "#EFCFCA" },
    /** MUI `warning`. Also the "Draft" status. */
    warning: { bg: "#FBF1DF", fg: "#875609", bd: "#EEDCB8" },
    /** MUI `default`. The "Archived" status. */
    archived: { bg: "#ECEFF0", fg: "#4A5556" },
    /** MUI `primary`. Also the selected menu row and light in-page nav row. */
    selected: { bg: "#E2F2F4", fg: "#00707D" },
    /** MUI `accent`. The count pill beside a section header. */
    count: { bg: "#EBDCD1", fg: "#AE4C1C" },
    /**
     * MUI `info`. The one status pair the toast design doc does not cover: it
     * draws info in teal (`#E7EFF1`/`#005E69` — `primary.dark`), and teal is
     * this app's banner and selection colour, not its status-info colour. Info
     * stays blue here, so `bd` is COMPUTED (this pair mixed 14% toward `fg`),
     * not specified by design. Send it back to design for confirmation.
     */
    info: { bg: "#EAF1FA", fg: "#134A86", bd: "#CCDAEA" },
  },
  /**
   * Avatar and stripe hues. Cycle in order by index so two adjacent members
   * never land on the same colour — the order is the specification, not an
   * arbitrary list, so consumers index into it rather than pick.
   */
  avatarHues: [
    "#007E8C",
    "#AE4C1C",
    "#3E8F77",
    "#7D5FC7",
    "#A83226",
    "#D98E12",
    "#5C8F95",
  ],
  common: {
    white: "#FFFFFF",
    black: "#000000",
  },
  action: {
    selected: "#F4F3F0",
    disabledBackground: "#E7E5E0",
    disabled: "#87908F",
    hoverOpacity: 0.02,
    hover: "rgba(0, 0, 0, 0.03)",
  },
} satisfies PaletteOptions;

export default palette;
